import {
  DataGrid,
  formatCurrency,
  formatDate,
  formatPercent,
  StatusBadge
} from '@ijoin/components';
import {
  GetAdminPlanEquityWashRuleRes,
  useGetAdminPlanEquityWashRules,
  useGetEnrollmentFundBalances
} from '@ijoin/hooks';
import {
  Card,
  CardContent,
  CardHeader,
  Link,
  Stack,
  Tooltip
} from '@mui/material';

import { camelCase } from 'lodash';
import { FC } from 'react';

const describeFund = (description: null | string, cusip: string) =>
  description ?? cusip;

const analysisUrlColumn = {
  field: 'analysisUrl',
  headerName: 'Analysis',
  renderCell: (params: {
    row: { description: string };
    value?: null | string;
  }) =>
    params.value ? (
      <Link
        data-testid={camelCase(`analysisUrl ${params.row.description}`)}
        href={params.value}
        rel='noopener'
        target='_blank'>
        Analysis
      </Link>
    ) : null,
  sortable: false,
  width: 100
};

const EquityWashCell: FC<{
  balanceByCusip: Map<string, number>;
  cusip?: null | string;
  rules: GetAdminPlanEquityWashRuleRes[];
}> = props => {
  if (!props.cusip) {
    return null;
  }

  const sourceRules = props.rules.filter(
    rule => rule.assetsFromFundId === props.cusip
  );
  const destinationRules = props.rules.filter(
    rule => rule.assetsToFundId === props.cusip
  );

  if (!sourceRules.length && !destinationRules.length) {
    return null;
  }

  const heldBalance = props.balanceByCusip.get(props.cusip) ?? 0;
  const blockedInbound = destinationRules.reduce(
    (total, rule) =>
      total + (props.balanceByCusip.get(rule.assetsFromFundId) ?? 0),
    0
  );

  return (
    <Stack
      alignItems='flex-start'
      spacing={0.5}
      sx={{ justifyContent: 'center', minHeight: '100%', py: 0.5 }}>
      {!!sourceRules.length && (
        <Tooltip
          title={`Competing fund: the ${formatCurrency(heldBalance)} held here cannot transfer directly to ${sourceRules
            .map(rule =>
              describeFund(rule.assetsToFundDescription, rule.assetsToFundId)
            )
            .join(', ')} — it must wash through equities first`}>
          <span>
            <StatusBadge severity='warning'>
              {`Wash Source · ${formatCurrency(heldBalance)}`}
            </StatusBadge>
          </span>
        </Tooltip>
      )}
      {!!destinationRules.length && (
        <Tooltip
          title={`Wash destination: direct transfers in are blocked from ${destinationRules
            .map(
              rule =>
                `${describeFund(rule.assetsFromFundDescription, rule.assetsFromFundId)} (${formatCurrency(props.balanceByCusip.get(rule.assetsFromFundId) ?? 0)})`
            )
            .join(', ')}`}>
          <span>
            <StatusBadge severity='info'>
              {`Wash Destination · ${formatCurrency(blockedInbound)} blocked`}
            </StatusBadge>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};

export const EnrollmentFundBalancesSection: FC<{
  planEnrollmentId: string;
  planId: string;
}> = props => {
  const fundBalances = useGetEnrollmentFundBalances(
    props.planId,
    props.planEnrollmentId
  );
  const equityWashRules = useGetAdminPlanEquityWashRules(props.planId);

  const now = new Date();
  const activeRules = (equityWashRules.data ?? []).filter(
    rule => new Date(rule.startDate) <= now && new Date(rule.endDate) >= now
  );

  const balanceByCusip = (fundBalances.data?.data ?? []).reduce(
    (totals, row) =>
      row.cusip
        ? totals.set(
            row.cusip,
            (totals.get(row.cusip) ?? 0) + (row.balance ?? 0)
          )
        : totals,
    new Map<string, number>()
  );

  const equityWashColumn = {
    field: 'equityWash',
    flex: 1,
    headerName: 'Equity Wash',
    renderCell: (params: { row: { cusip?: null | string } }) => (
      <EquityWashCell
        balanceByCusip={balanceByCusip}
        cusip={params.row.cusip}
        rules={activeRules}
      />
    ),
    sortable: false
  };

  return (
    <Stack spacing={2}>
      <Card sx={{ p: 1 }}>
        <CardHeader
          subheader='Where money currently sits, as reported by the record keeper'
          title='Fund Balances'
        />
        <CardContent>
          <DataGrid
            columns={[
              {
                field: 'description',
                flex: 1,
                headerName: 'Description'
              },
              {
                field: 'tickerSymbol',
                headerName: 'Ticker',
                width: 100
              },
              {
                field: 'cusip',
                headerName: 'CUSIP',
                width: 120
              },
              analysisUrlColumn,
              {
                field: 'balance',
                headerName: 'Balance',
                valueFormatter: value => formatCurrency(value ?? 0),
                width: 140
              },
              {
                field: 'shares',
                headerName: 'Shares',
                width: 100
              },
              {
                field: 'externalModelId',
                flex: 0.75,
                headerName: 'External Model ID'
              },
              {
                field: 'pathTypes',
                flex: 1.25,
                headerName: 'Path Type',
                valueGetter: (_value, row) => row.pathTypes.join(', ')
              },
              ...(activeRules.length ? [equityWashColumn] : []),
              {
                field: 'updatedAt',
                headerName: 'Updated',
                valueFormatter: value => (value ? formatDate(value) : ''),
                width: 140
              },
              {
                align: 'center',
                field: 'isPartialMap',
                headerAlign: 'center',
                headerName: 'Partial MAP',
                renderCell: params =>
                  params.value ? (
                    <StatusBadge severity='success'>Partial MAP</StatusBadge>
                  ) : null,
                sortable: false,
                width: 100
              }
            ]}
            disableColumnMenu
            disableRowSelectionOnClick
            getRowId={row => row.participantFundBalanceId}
            loading={fundBalances.isFetching}
            pageSizeOptions={[25, 50, 100]}
            pinnedColumns={{ left: ['description'] }}
            rows={fundBalances.data?.data ?? []}
            showColumnMenu
            slotProps={{
              root: {
                'data-component': 'dataGrid',
                'data-testid': 'enrollmentFundBalances'
              }
            }}
          />
        </CardContent>
      </Card>
      <Card sx={{ p: 1 }}>
        <CardHeader
          subheader='Where future contributions are directed by the current election'
          title='Allocations'
        />
        <CardContent>
          <DataGrid
            columns={[
              {
                field: 'description',
                flex: 1,
                headerName: 'Description'
              },
              {
                field: 'tickerSymbol',
                headerName: 'Ticker',
                width: 100
              },
              {
                field: 'cusip',
                headerName: 'CUSIP',
                width: 120
              },
              analysisUrlColumn,
              {
                field: 'percentAllocation',
                headerName: 'Allocation %',
                valueFormatter: value =>
                  formatPercent(value ?? 0, { fractionDigits: 2 }),
                width: 140
              },
              {
                field: 'pathType',
                flex: 1.25,
                headerName: 'Path Type'
              },
              ...(activeRules.length ? [equityWashColumn] : [])
            ]}
            data-testid='enrollmentAllocationsGrid'
            disableColumnMenu
            disableRowSelectionOnClick
            getRowId={row => row.fundId}
            loading={fundBalances.isFetching}
            pageSizeOptions={[25, 50, 100]}
            pinnedColumns={{ left: ['description'] }}
            rows={fundBalances.data?.allocations ?? []}
          />
        </CardContent>
      </Card>
    </Stack>
  );
};

EnrollmentFundBalancesSection.displayName = 'EnrollmentFundBalancesSection';
